// Read-side display of saved Deformation Analysis results on the
// inspection-detail page. Renders one top-level card per kind
// ("Shell Deformation Analysis" / "Floor Deformation Analysis") with
// a sub-card per standard (currently only "EEMUA 159"; API 653 will
// slot in here automatically once it lands).
//
// Data source: GET /api/inspections/{id}/defo-results, populated by
// the Save-and-close handler on the defo popup pages.

import React, { useEffect, useState } from 'react';
import { get } from '../api';
import './DefoResultsCard.css';


// OOV percentage → 3-tier band (matches the defo page).
function oovTier(pct) {
  if (pct == null) return '';
  if (pct < 80)  return 'ok';
  if (pct <= 90) return 'warn';
  return 'fail';
}

function fmtMm(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return Math.round(v).toLocaleString();
}

function NominalGeometryTable({ payload }) {
  const n = payload?.nominal || {};
  const b = payload?.bounds || {};
  const poolPct = (n.nominalPoolCount != null && n.totalCount)
    ? (100 * n.nominalPoolCount / n.totalCount).toFixed(1)
    : null;
  return (
    <table className="defo-stats-table defo-nominal-table">
      <tbody>
        {payload?.pointCount != null && (
          <tr><th>Points parsed and read</th><td>{payload.pointCount.toLocaleString()}</td></tr>
        )}
        {n.nominalPoolCount != null && n.totalCount != null && (
          <tr>
            <th>Nominal pool</th>
            <td>
              {n.nominalPoolCount.toLocaleString()} / {n.totalCount.toLocaleString()}
              {poolPct != null && <> ({poolPct} %)</>}
            </td>
          </tr>
        )}
        {b.minX != null && (
          <tr><th>X bounds (mm)</th>
            <td>{fmtMm(b.minX * 1000)} → {fmtMm(b.maxX * 1000)}</td>
          </tr>
        )}
        {b.minY != null && (
          <tr><th>Y bounds (mm)</th>
            <td>{fmtMm(b.minY * 1000)} → {fmtMm(b.maxY * 1000)}</td>
          </tr>
        )}
        {b.minZ != null && (
          <tr><th>Z bounds (mm)</th>
            <td>{fmtMm(b.minZ * 1000)} → {fmtMm(b.maxZ * 1000)}</td>
          </tr>
        )}
        {payload?.binsPerCircle != null && (
          <tr><th>θ bins</th><td>{payload.binsPerCircle} × {payload.thetaBinDeg}°</td></tr>
        )}
        {n.R_mm != null && <tr><th>Nominal radius (R)</th><td>{n.R_mm.toLocaleString()} mm</td></tr>}
        {n.D_mm != null && <tr><th>Nominal diameter (D = 2R)</th><td>{n.D_mm.toLocaleString()} mm</td></tr>}
        {n.H_mm != null && <tr><th>Nominal height (H)</th><td>{n.H_mm.toLocaleString()} mm</td></tr>}
        {payload?.durationMs != null && (
          <tr><th>Total time</th><td>{(payload.durationMs / 1000).toFixed(2)} s</td></tr>
        )}
      </tbody>
    </table>
  );
}

function OOVTable({ oov }) {
  if (!Array.isArray(oov) || oov.length === 0) return null;
  return (
    <>
      <table className="defo-oov-table defo-oov-table-display">
        <thead>
          <tr>
            <th>Scanline (Angle°)</th>
            <th>R bottom corner-weld (mm)</th>
            <th>R top corner-weld (mm)</th>
            <th>Out of Verticality (mm)</th>
            <th>Allowable limit (mm)</th>
            <th>Percentage deviation from limit (%)</th>
          </tr>
        </thead>
        <tbody>
          {oov.map((r) => r.muted ? (
            // Scanline switched off on the analysis page → one merged cell.
            <tr key={r.scanline} className="defo-oov-muted">
              <td>
                {r.scanline} ({r.angle}°
                {r.snapped !== r.angle && (
                  <span className="defo-chart-snap"> → {r.snapped}°</span>
                )})
              </td>
              <td colSpan={5}>Scanline was not computed</td>
            </tr>
          ) : (
            <tr key={r.scanline}>
              <td>
                {r.scanline} ({r.angle}°
                {r.snapped !== r.angle && (
                  <span className="defo-chart-snap"> → {r.snapped}°</span>
                )})
              </td>
              <td>{fmtMm(r.rBottom)}</td>
              <td>{fmtMm(r.rTop)}</td>
              <td>{fmtMm(r.oov)}</td>
              <td>{fmtMm(r.allow)}</td>
              <td className={`defo-oov-pct ${oovTier(r.pct)}`}>
                {r.pct != null ? `${r.pct.toFixed(1)} %` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ImageGrid({ images, prefix }) {
  // Filter+sort scanline_1, scanline_2, ...
  const list = (images || [])
    .filter((img) => img.name.startsWith(prefix + '_'))
    .map((img) => ({
      ...img,
      n: parseInt(img.name.slice(prefix.length + 1), 10) || 0,
    }))
    .sort((a, b) => a.n - b.n);
  if (list.length === 0) return null;
  return (
    <div className="defo-result-image-grid">
      {list.map((img) => (
        <figure key={img.name} className="defo-result-image-fig">
          <img src={img.url} alt={img.name} loading="lazy" />
          <figcaption>
            {prefix === 'scanline'
              ? `Scanline ${img.n}`
              /* Floor scanlines are paired opposite: with P pair-images there
                 are 2P scanlines, so pair n = scanlines n and n+P (e.g. 1-5). */
              : `Scanline ${img.n}-${img.n + list.length}`}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function FullWidthImage({ images, name, title }) {
  const img = (images || []).find((i) => i.name === name);
  if (!img) return null;
  return (
    <figure className="defo-result-image-full">
      <img src={img.url} alt={name} loading="lazy" />
      {title && <figcaption>{title}</figcaption>}
    </figure>
  );
}

// EEMUA 159 basis for the OOV limit, filled from the saved nominal geometry
// (whole mm). Mirrors _defo_oov_basis in backend/reports/builder.py (PDF).
function OOVBasis({ payload }) {
  const n = payload?.nominal;
  if (!n || !Number.isFinite(n.H_mm)) return null;
  return (
    <p className="defo-result-section-text">
      In accordance with EEMUA 159 (Edition 6), Section 6.5.1, the out-of-verticality
      at the top of the tank shell should not exceed 1% of the total shell height.
      The computed nominal shell height is {fmtMm(n.H_mm)} mm; therefore, the maximum
      allowable out-of-verticality is {fmtMm(n.H_mm * 0.01)} mm.
    </p>
  );
}

// Auto-notes for a shell result — always shown. Mirrors _defo_shell_notes
// in backend/reports/builder.py.
const NEGATIVE_OOV_NOTE =
  'A negative out-of-verticality indicates that the scanline of the particular tank shell faces inwards.';
function shellAutoNotes() {
  return [NEGATIVE_OOV_NOTE];
}

// "Notes" block, same look as the UT sub-card notes: auto-notes first, then
// the user's notes from Edit Analysis (one per line, de-duplicated).
function AnalysisNotes({ notes = '', auto = [] }) {
  const seen = new Set(auto.map((n) => n.toLowerCase()));
  const user = (notes || '').split('\n').map((l) => l.trim()).filter((l) => {
    if (!l) return false;
    const k = l.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const all = [...auto, ...user];
  if (all.length === 0) return null;
  return (
    <div className="insp-ut-subcard-notes">
      <div className="insp-ut-subcard-notes-title">Notes</div>
      <ul className="insp-ut-subcard-notes-list">
        {all.map((n, i) => <li key={i}>{n}</li>)}
      </ul>
    </div>
  );
}

function ShellSubcard({ row, notes }) {
  return (
    <div className="defo-result-sections">
      <h3 className="defo-result-section-title">Nominal geometry</h3>
      <NominalGeometryTable payload={row.payload} />

      <h3 className="defo-result-section-title">Out of Verticality (OOV)</h3>
      {(row.standard || 'eemua159') === 'eemua159' && <OOVBasis payload={row.payload} />}
      <OOVTable oov={row.payload?.oov} />
      <AnalysisNotes notes={notes} auto={shellAutoNotes()} />

      <h3 className="defo-result-section-title">Scanline shell profiles</h3>
      <ImageGrid images={row.images} prefix="scanline" />

      <h3 className="defo-result-section-title">Unrolled Shell Deformation Heatmap</h3>
      <FullWidthImage images={row.images} name="c2m_unrolled" />
    </div>
  );
}

function FloorSubcard({ row, notes }) {
  const s = row.payload?.settlement || {};
  return (
    <div className="defo-result-sections">
      {/* Order: nominal geometry → settlements → profiles → heatmap */}
      <h3 className="defo-result-section-title">Nominal geometry</h3>
      <NominalGeometryTable payload={row.payload} />

      {Array.isArray(s.local) && s.local.length > 0 && (
        <>
          <h3 className="defo-result-section-title">Local differential settlement</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="defo-oov-table defo-oov-table-display" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Scanline (Angle°)</th>
                  <th>Relative height (mm)</th>
                  <th>Absolute height difference to the left scanline (mm)</th>
                  <th>Absolute height difference to the right scanline (mm)</th>
                  <th>Maximum height difference to adjacent scanline (mm)</th>
                  <th>Allowable limit (mm)</th>
                  <th>Percentage deviation from limit (%)</th>
                </tr>
              </thead>
              <tbody>
                {s.local.map((r) => (
                  <tr key={r.scanlineN}>
                    <td>{r.scanlineN} ({r.angle}°)</td>
                    <td>{fmtMm(r.zRim)}</td>
                    <td>{fmtMm(r.dLeft)}</td>
                    <td>{fmtMm(r.dRight)}</td>
                    <td>{fmtMm(r.dMax)}</td>
                    <td>{fmtMm(r.allowable)}</td>
                    <td className={`defo-oov-pct ${oovTier(r.pct)}`}>
                      {r.pct != null ? `${r.pct.toFixed(1)} %` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {s.general && s.general.diff != null && (
        <>
          <h3 className="defo-result-section-title">General differential settlement</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="defo-oov-table defo-oov-table-display" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Z<sub>max</sub> (mm)</th>
                  <th>At scanline(s)</th>
                  <th>Z<sub>min</sub> (mm)</th>
                  <th>At scanline(s)</th>
                  <th>Difference (mm)</th>
                  <th>S<sub>max</sub> (mm)</th>
                  <th>Percentage deviation from limit (%)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{fmtMm(s.general.zMax)}</td>
                  <td>{(s.general.maxScanlines || []).length ? s.general.maxScanlines.join(', ') : '—'}</td>
                  <td>{fmtMm(s.general.zMin)}</td>
                  <td>{(s.general.minScanlines || []).length ? s.general.minScanlines.join(', ') : '—'}</td>
                  <td>{fmtMm(s.general.diff)}</td>
                  <td>{fmtMm(s.general.sMax)}</td>
                  <td className={`defo-oov-pct ${oovTier(s.general.pct)}`}>
                    {s.general.pct != null ? `${s.general.pct.toFixed(1)} %` : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {Array.isArray(s.edge) && s.edge.length > 0 && (
        <>
          <h3 className="defo-result-section-title">Edge settlement</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="defo-oov-table defo-oov-table-display" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Scanline (Angle°)</th>
                  <th>Breakpoint distance (mm)</th>
                  <th>Allowable limit (mm)</th>
                  <th>Measured edge settlement (mm)</th>
                  <th>Percentage deviation from limit (%)</th>
                </tr>
              </thead>
              <tbody>
                {s.edge.map((r) => (
                  <tr key={r.scanlineN}>
                    <td>{r.scanlineN} ({r.angle}°)</td>
                    <td>{fmtMm(r.breakpoint)}</td>
                    <td>{fmtMm(r.allowable)}</td>
                    <td>{fmtMm(r.measured)}</td>
                    <td className={`defo-oov-pct ${oovTier(r.pct)}`}>
                      {r.pct != null ? `${r.pct.toFixed(1)} %` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AnalysisNotes notes={notes} />

      <h3 className="defo-result-section-title">Scanline Floor Profiles</h3>
      <ImageGrid images={row.images} prefix="pair" />

      <h3 className="defo-result-section-title">Un-tilted Floor Deformation Heatmap</h3>
      <FullWidthImage images={row.images} name="c2m_floor" />
    </div>
  );
}

/** Render the saved (standard) sub-cards for ONE kind (shell or
 *  floor), inline. No outer wrapper / heading — that's owned by the
 *  containing `.insp-defo-link-card`. Returns null when there are no
 *  results yet so the parent card still reads clean.
 *
 *  Refetches automatically when the tab regains focus — covers the
 *  "Save and close in popup → switch back to inspection tab" path
 *  without forcing a manual reload. */
export function DefoSubcards({ inspectionId, kind, notes = '' }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [reloadNonce, setReloadNonce] = useState(0);

  // Refetch on focus so closing the defo popup → coming back here
  // surfaces the new row without a manual page refresh.
  useEffect(() => {
    const onFocus = () => setReloadNonce((n) => n + 1);
    window.addEventListener('focus', onFocus);
    // Save-and-close in the analysis tab broadcasts here so results refresh
    // immediately, without relying on the (unreliable) focus event.
    let ch;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        ch = new BroadcastChannel('tib-defo-results');
        ch.onmessage = () => setReloadNonce((n) => n + 1);
      }
    } catch { /* best-effort */ }
    return () => {
      window.removeEventListener('focus', onFocus);
      if (ch) ch.close();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError('');
    get(`/inspections/${inspectionId}/defo-results`)
      .then((data) => { if (!cancelled) setRows(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load defo results'); });
    return () => { cancelled = true; };
  }, [inspectionId, reloadNonce]);

  const filtered = rows.filter((r) => r.kind === kind);
  if (filtered.length === 0 && !error) return null;

  return (
    <>
      {error && <div className="defo-error" style={{ margin: '12px 0' }}>{error}</div>}
      {filtered.map((row) => (
        kind === 'shell'
          ? <ShellSubcard key={row.id} row={row} notes={notes} />
          : <FloorSubcard key={row.id} row={row} notes={notes} />
      ))}
    </>
  );
}

// Legacy default export kept for compatibility — was the
// "everything" wrapper used in v3.20.0 before the per-kind redesign.
// Returns null so it can stay in JSX without rendering anything.
export default function DefoResultsCard() {
  return null;
}
